<template>
  <div class="c-profile-content-list__container">
    <div
      v-if="isLoading"
      class="c-loading__container u-mbm"
    >
      <SVGIcon class="loading-icon" width="80" height="80" name="loading" />
    </div>
    <template v-else>
      <!-- user articles and events -->
      <template v-if="userContent && userContent.length">
        <div class="c-profile-content-list__carousel-container">
          <div v-if="splideCarousel && splideCarousel.splide" class="carousel__navigation">
            <button
              type="button"
              :class="currentSlideIndex === 0 ? 'carousel__navigation-button--disabled' : ''"
              class="carousel__navigation-button u-mrs"
              :disabled="currentSlideIndex === 0"
              @click="splideCarousel.go('<')"
            >
              <SVGIcon class="c-button__icon" name="arrow-left-white" />
            </button>
          </div>
          <Splide ref="splideCarousel" :has-track="false" :options="{ rewind: false, perPage: maxSlidesPerPage, gap: '1rem', breakpoints: breakpoints, arrows: false, pagination: false }" aria-label="Carousel">
            <SplideTrack>
              <SplideSlide
                v-for="content in userContent"
                :key="content.data.pk"
              >
                <template v-if="content.data">
                  <a v-if="content.data.status === 2 || (content.data.status === 1 && isDraftVisible)" class="c-profile-content-list__card-link c-carousel__card-link" :href="parseContentUrl(content.type, content.data.slug)">
                    <div class="carousel__item">
                      <template v-if="content.data.images[0]">
                        <img
                          :class="content.data.images[0].thumbnail_368_256_crop && urlExists(content.data.images[0].thumbnail_368_256_crop) ? '' : 'c-profile-content-list__card-thumbnail-placeholder'"
                          class="c-profile-content-list__card-thumbnail"
                          :src="content.data.images[0].thumbnail_368_256_crop && urlExists(content.data.images[0].thumbnail_368_256_crop) ? content.data.images[0].thumbnail_368_256_crop : baseUrl + 'icons/logo-forum-black.svg'"
                          alt="card thumbnail"
                        >
                      </template>
                      <img
                        v-else
                        class="c-profile-content-list__card-thumbnail c-profile-content-list__card-thumbnail-placeholder"
                        :src="baseUrl + 'icons/logo-forum-black.svg'"
                        alt="card thumbnail"
                      >
                      <div :class="'profile-content-list-' + content.type.toLowerCase()" class="c-card__text c-profile-content-list__card-content">
                        <p>{{ content.type.charAt(0).toUpperCase() + content.type.slice(1) }}</p>
                      </div>
                      <h3 class="c-profile-content-list__card-title">{{ content.data.title }}</h3>
                      <div class="c-card__text c-profile-content-list__card-description">
                        {{ truncateString(content.type.toLowerCase() === 'project' ? filterDescriptionBanner(content.data.description) : content.data.description, 130) }}
                      </div>
                      <div v-if="content.type.toLowerCase() === 'event'" class="c-profile-content-list__card-date">
                        <!-- if event is ending the same day -->
                        <template v-if="content.data.end && (dateFromISO(content.data.start) === dateFromISO(content.data.end))">
                          <ul v-if="content.data.start" class="c-profile-content-list__card-metas">
                            <li class="c-profile-content-list__card-meta">
                              <SVGIcon class="c-profile-content-list__card-icon" name="calendar" />
                              <span class="c-profile-content-list__card-label">{{ dateFromISO(content.data.start) }}</span>
                            </li>
                            <li class="c-profile-content-list__card-meta">
                              <SVGIcon class="c-profile-content-list__card-icon" name="time" />
                              <span class="c-profile-content-list__card-label">{{ timeFromISO(content.data.start) }}</span>
                              <span v-if="content.data.end" class="c-profile-content-list__card-label"> - {{ timeFromISO(content.data.end) }}</span>
                            </li>
                          </ul>
                        </template>
                        <!-- else if event is ending on different day -->
                        <ul v-else class="c-profile-content-list__card-metas">
                          <li v-if="content.data.start" class="c-profile-content-list__card-meta">
                            <SVGIcon class="c-profile-content-list__card-icon" name="calendar" />
                            <span class="c-profile-content-list__card-label">{{ dateFromISO(content.data.start) }}</span>
                            <span v-if="content.data.end" class="c-profile-content-list__card-label"> - {{ dateFromISO(content.data.end) }}</span>
                          </li>
                          <li v-if="content.data.start" class="c-profile-content-list__card-meta u-mtxs">
                            <SVGIcon class="c-profile-content-list__card-icon" name="time" />
                            <span class="c-profile-content-list__card-label">{{ timeFromISO(content.data.start) }}</span>
                            <span v-if="content.data.end" class="c-profile-content-list__card-label u-mlxs">- {{ timeFromISO(content.data.end) }}</span>
                          </li>
                        </ul>
                      </div>
                      <template v-if="content.data.topics">
                        <ul v-if="content.data.topics.length" class="c-profile-content-list__card-topics">
                          <template v-for="(topic, index) in content.data.topics">
                            <a
                              v-if="index < topicsMax"
                              :key="topic.id"
                              :href="'/search/?topics=' + topic.name" class="c-card__topic-link">
                              <li class="c-profile-content-list__card-topic">
                                <p>{{ truncateString(topic.name, 15) }}</p>
                              </li>
                            </a>
                          </template>
                        </ul>
                      </template>
                      <div class="c-profile-content-list__card-footer">
                        <div v-if="content.type !== 'Project'" class="c-profile-content-list__avatar">
                          <template v-if="content.author">
                            <!-- content author avatar -->
                            <template v-if="content.author.forum_user.avatar_url && urlExists(content.author.forum_user.avatar_url)">
                              <a :href="'/profile/' + content.author.username + '/'" class="c-profile-content-list__avatar-link">
                                <SVGIcon class="c-profile-content-list__avatar-border" width="32" height="32" name="avatar-outline-thick" />
                                <img
                                  :src="content.author.forum_user.avatar_url"
                                  alt=""
                                  class="c-profile-content-list__avatar-image"
                                >
                                <SVGIcon v-if="content.author.forum_user.is_premium" class="c-profile-content-list__avatar-premium-icon" name="diamond-small" />
                              </a>
                            </template>
                            <template v-else>
                              <!-- if no avatar image -->
                              <a :href="'/profile/' + content.author.username + '/'" class="c-profile-content-list__avatar-link">
                                <img :src="baseUrl + 'icons/avatar.svg'" alt="" class="c-profile-content-list__avatar-image c-profile-content-list__avatar-image-placeholder">
                                <SVGIcon v-if="content.author.forum_user.is_premium" class="c-profile-content-list__avatar-premium-icon" name="diamond-small" />
                              </a>
                            </template>
                          </template>
                          <template v-else>
                            <!-- placeholder if no content author -->
                            <img
                              :src="baseUrl + 'icons/avatar.svg'"
                              alt=""
                              class="c-profile-content-list__avatar-image c-profile-content-list__avatar-image-placeholder"
                            >
                          </template>
                        </div>
                        <div class="c-profile-content-list__card-star">
                          <Bookmark
                            :content-id="content.data.pk"
                            :content-name="pluralize(content.type.toLowerCase())"
                          />
                        </div>
                      </div>
                    </div>
                  </a>
                </template>
              </SplideSlide>
            </SplideTrack>
            <div v-if="splideCarousel && splideCarousel.splide" class="carousel__navigation carousel__navigation-bottom">
              <button
                type="button"
                class="carousel__navigation-button u-mrs"
                @click="splideCarousel.go('<')"
              >
                <SVGIcon class="c-button__icon" name="arrow-left-white" />
              </button>
              <button
                type="button"
                class="carousel__navigation-button"
                @click="splideCarousel.go('>')"
              >
                <SVGIcon class="c-button__icon" name="arrow-right-white" />
              </button>
            </div>
          </Splide>
          <div v-if="splideCarousel && splideCarousel.splide" class="carousel__navigation">
            <button
              type="button"
              :class="(currentSlideIndex + maxSlidesPerPage) >= splideCarousel.length ? 'carousel__navigation-button--disabled' : ''"
              class="carousel__navigation-button u-mls"
              :disabled="(currentSlideIndex + maxSlidesPerPage) >= splideCarousel.length"
              @click="splideCarousel.go('>')"
            >
              <SVGIcon class="c-button__icon" name="arrow-right-white" />
            </button>
          </div>
        </div>
      </template>
      <!-- user projects -->
      <template v-if="userProjects && userProjects.length">
        <div class="c-profile-content-list__carousel-container">
          <div v-if="splideCarousel && splideCarousel.splide" class="carousel__navigation">
            <button
              type="button"
              :class="currentSlideIndex === 0 ? 'carousel__navigation-button--disabled' : ''"
              class="carousel__navigation-button u-mrs"
              :disabled="currentSlideIndex === 0"
              @click="splideCarousel.go('<')"
            >
              <SVGIcon class="c-button__icon" name="arrow-left-white" />
            </button>
          </div>
          <Splide ref="splideCarousel" :has-track="false" :options="{ rewind: false, perPage: maxSlidesPerPage, gap: '1rem', breakpoints: breakpoints, arrows: false, pagination: false }" aria-label="Carousel">
            <SplideTrack>
              <SplideSlide
                v-for="content in userProjects"
                :key="content.data.pk"
              >
                <template v-if="content.data">
                  <a v-if="content.data.status === 'published' || (content.data.status === 'draft' && isDraftVisible)" class="c-profile-content-list__card-link c-carousel__card-link" :href="parseContentUrl(content.type, content.data.slug)">
                    <div class="carousel__item">
                      <template v-if="content.data.images[0]">
                        <img
                          :class="content.data.images[0].thumbnail_368_256_crop && urlExists(content.data.images[0].thumbnail_368_256_crop) ? '' : 'c-profile-content-list__card-thumbnail-placeholder'"
                          class="c-profile-content-list__card-thumbnail"
                          :src="content.data.images[0].thumbnail_368_256_crop && urlExists(content.data.images[0].thumbnail_368_256_crop) ? content.data.images[0].thumbnail_368_256_crop : baseUrl + 'icons/logo-forum-black.svg'"
                          alt="card thumbnail"
                        >
                      </template>
                      <img
                        v-else
                        class="c-profile-content-list__card-thumbnail c-profile-content-list__card-thumbnail-placeholder"
                        :src="baseUrl + 'icons/logo-forum-black.svg'"
                        alt="card thumbnail"
                      >
                      <div class="u-flex">
                        <div :class="'profile-content-list-' + content.type.toLowerCase()" class="c-card__text c-profile-content-list__card-content">
                          <p>{{ content.type.charAt(0).toUpperCase() + content.type.slice(1) }}</p>
                        </div>
                        <template v-if="content.type.toLowerCase() === 'project' && content.data.is_premium">
                          <div class="c-card__project-premium">
                            <img class="c-card__icon c-card__premium-icon" :src="baseUrl + 'icons/diamond-small.svg'" aria-hidden="true" alt="diamond icon">
                            <label>Premium</label>
                          </div>
                        </template>
                      </div>
                      <h3 class="c-profile-content-list__card-title">{{ content.data.title }}</h3>
                      <div class="c-card__text c-profile-content-list__card-description">
                        {{ truncateString(content.type.toLowerCase() === 'project' ? filterDescriptionBanner(content.data.description) : content.data.description, 130) }}
                      </div>
                      <template v-if="content.data.topics">
                        <ul v-if="content.data.topics.length" class="c-profile-content-list__card-topics">
                          <template v-for="(topic, index) in content.data.topics">
                            <a
                              v-if="index < topicsMax"
                              :key="topic.id"
                              :href="'/search/?topics=' + topic.name" class="c-card__topic-link">
                              <li class="c-profile-content-list__card-topic">
                                <p>{{ truncateString(topic.name, 15) }}</p>
                              </li>
                            </a>
                          </template>
                        </ul>
                      </template>
                      <div class="c-profile-content-list__card-footer">
                        <div class="c-profile-content-list__avatar c-profile-content-list__avatar-contributors">
                          <ProjectContributors
                            :project-id="content.data.pk"
                            theme="card"
                            limit="4"
                          />
                        </div>
                        <div class="c-profile-content-list__card-star">
                          <Bookmark
                            :content-id="content.data.pk"
                            :content-name="pluralize(content.type.toLowerCase())"
                          />
                        </div>
                      </div>
                    </div>
                  </a>
                </template>
              </SplideSlide>
            </SplideTrack>
            <div v-if="splideCarousel && splideCarousel.splide" class="carousel__navigation carousel__navigation-bottom">
              <button
                type="button"
                class="carousel__navigation-button u-mrs"
                @click="splideCarousel.go('<')"
              >
                <SVGIcon class="c-button__icon" name="arrow-left-white" />
              </button>
              <button
                type="button"
                class="carousel__navigation-button"
                @click="splideCarousel.go('>')"
              >
                <SVGIcon class="c-button__icon" name="arrow-right-white" />
              </button>
            </div>
          </Splide>
          <div v-if="splideCarousel && splideCarousel.splide" class="carousel__navigation">
            <button
              type="button"
              :class="(currentSlideIndex + maxSlidesPerPage) >= splideCarousel.length ? 'carousel__navigation-button--disabled' : ''"
              class="carousel__navigation-button u-mls"
              :disabled="(currentSlideIndex + maxSlidesPerPage) >= splideCarousel.length"
              @click="splideCarousel.go('>')"
            >
              <SVGIcon class="c-button__icon" name="arrow-right-white" />
            </button>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import {
  getUser,
  TopicStatus,
  ContentType,
  getArticle,
  getEvent,
  getProjectLight
} from '@/utils/api'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { truncateString, pluralize, filterDescriptionBanner } from '@/utils/string'
import { urlExists, parseContentUrl } from '@/utils/urls'
import { dateFromISO, timeFromISO } from '@/utils/time'
import ProjectContributors from '@/components/ProjectContributors.ce.vue'
import Bookmark from '@/components/Bookmark.ce.vue'
import SVGIcon from '@/components/SVGIcon.vue'

import '@splidejs/vue-splide/css/core'

const props = defineProps<{
  contentType: string
  contentPks?: string
  canViewDrafts?: string
}>()
const isLoading = ref(true)

const baseUrl = '/static/'

const contentType = ref(props.contentType.toLowerCase())

const userContent = ref<any[]>([])
const userContentPks = ref()

const userProjects = ref<any[]>([])
const userProjectsPks = ref()

const isDraftVisible = ref(false)
if (props.canViewDrafts === 'True') {
  isDraftVisible.value = true
} else {
  isDraftVisible.value = false
}

const topicsMax = ref(5)

const splideCarousel = ref()

const currentSlideIndex = ref(0)
const maxSlidesPerPage = ref(4)

const breakpoints = {
  300: {
    perPage: 1,
    gap: '1rem'
  },
  800: {
    perPage: 1,
    gap: '1rem'
  },
  990: {
    perPage: 2,
    gap: '1rem'
  },
  1240: {
    perPage: 3,
    gap: '1rem'
  },
  1300: {
    perPage: 4,
    gap: '1rem'
  }
}

function setMaxSlidesBreakpointsOnMount () {
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth

  if ((viewportWidth >= 300) && (viewportWidth < 800)) {
    maxSlidesPerPage.value = breakpoints[300].perPage
  }

  if ((viewportWidth >= 800) && (viewportWidth < 990)) {
    maxSlidesPerPage.value = breakpoints[800].perPage
  }

  if ((viewportWidth >= 990) && (viewportWidth < 1240)) {
    maxSlidesPerPage.value = breakpoints[990].perPage
  }

  if ((viewportWidth >= 1240) && (viewportWidth < 1300)) {
    maxSlidesPerPage.value = breakpoints[1240].perPage
  }

  if ((viewportWidth >= 1300)) {
    maxSlidesPerPage.value = breakpoints[1300].perPage
  }
}

watch([ splideCarousel ], async () => {
  if (splideCarousel.value) {
    // add carousel event listener
    splideCarousel.value.splide.on('moved', function (newIndex: number) {
      currentSlideIndex.value = newIndex
    })
  }
}, { immediate: true })

function parseContentPks (contentPksStr: string) {
  let pksStr = contentPksStr.substring(1)
  pksStr = pksStr.slice(0, -1)
  const pksArr = pksStr.split(', ')

  const pks = ref<number[]>([])
  for (let i = 0; i < pksArr.length; i++) {
    pks.value.push(parseInt(pksArr[i]))
  }

  return pks.value
}

async function getUserArticles (): Promise<any> {
  for (let i = 0; i < userContentPks.value.length; i++) {
    const id = userContentPks.value[i]

    try {
      const data = await getArticle(id)
      const articleResult = {
        id: id,
        type: ContentType.Article,
        topics: await parseTopics(data!),
        author: data!.user ? await getUser(data!.user as any) : null,
        data: data!
      }
      userContent.value.push(articleResult)
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }
      throw err
    }
  }
}

async function getUserEvents (): Promise<any> {
  for (let i = 0; i < userContentPks.value.length; i++) {
    const id = userContentPks.value[i]

    try {
      const data = await getEvent(id)
      const eventResult = {
        id: id,
        type: ContentType.Event,
        topics: await parseTopics(data!),
        author: data!.user ? await getUser(data!.user as any) : null,
        data: data!
      }
      userContent.value.push(eventResult)
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }
      throw err
    }
  }
}

async function getUserProjects (): Promise<any> {
  for (let i = 0; i < userProjectsPks.value.length; i++) {
    const id = userProjectsPks.value[i]

    try {
      const data = await getProjectLight(id)

      if (data) {
        const projectResult = {
          id: id,
          type: ContentType.Project,
          topics: await parseTopics(data!),
          author: data!.owner ? await getUser(data!.owner.pk) : null,
          data: data!
        }
        userProjects.value.push(projectResult)
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }
      throw err
    }
  }
}

async function parseTopics (contentData: any): Promise<any> {
  const content = ref(contentData)

  const topics = ref(content.value.topics)
  // console.log('ALL TOPICS: ', topics.value)

  const filteredTopics = ref(Array.from(topics.value).filter((topic: any) => {
    return topic.status === TopicStatus.accepted
  }))
  // get first n topics in filtered topics
  filteredTopics.value = filteredTopics.value.splice(0, topicsMax.value)
  // console.log('FILTERED TOPICS: ', filteredTopics.value)

  return filteredTopics.value
}

onMounted(async () => {
  setMaxSlidesBreakpointsOnMount()

  // parse articles
  if (contentType.value === 'article' && props.contentPks) {
    userContentPks.value = parseContentPks(props.contentPks)
    await getUserArticles()
  }

  // parse events
  if (contentType.value === 'event' && props.contentPks) {
    userContentPks.value = parseContentPks(props.contentPks)
    await getUserEvents()
  }

  // parse projects
  if (contentType.value === 'project' && props.contentPks) {
    userProjectsPks.value = parseContentPks(props.contentPks)
    await getUserProjects()
  }

  isLoading.value = false
})
</script>

<!-- <style scoped lang="scss">
</style> -->
